import React, { useState, useEffect } from 'react';
import { BOOTSTRAP_ICONS } from '../constants';
import sidePanelStyles from '../../../../styles/SidePanel.module.css';

interface IconPickerProps {
  selectedIcon: string;
  onIconSelect: (iconName: string) => void;
}

const IconPicker: React.FC<IconPickerProps> = ({ selectedIcon, onIconSelect }) => {
  const [iconSearchTerm, setIconSearchTerm] = useState('');
  const [selectedIconCategory, setSelectedIconCategory] = useState('all');
  const [filteredIcons, setFilteredIcons] = useState(BOOTSTRAP_ICONS);

  useEffect(() => {
    const filtered = BOOTSTRAP_ICONS.filter(icon => {
      const matchesCategory = selectedIconCategory === 'all' || icon.category === selectedIconCategory;
      const matchesSearch = iconSearchTerm === '' || 
        icon.name.toLowerCase().includes(iconSearchTerm.toLowerCase()) || 
        icon.label.toLowerCase().includes(iconSearchTerm.toLowerCase()) ||
        icon.category.toLowerCase().includes(iconSearchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
    setFilteredIcons(filtered);
  }, [iconSearchTerm, selectedIconCategory]);

  const getIconCategories = () => {
    const categories = ['all', ...Array.from(new Set(BOOTSTRAP_ICONS.map(icon => icon.category)))];
    return categories.map(cat => ({
      value: cat,
      label: cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)
    }));
  };

  const getSelectedIconInfo = () => {
    return BOOTSTRAP_ICONS.find(icon => icon.name === selectedIcon) || BOOTSTRAP_ICONS[0];
  };

  const IconComponent = ({ iconName, className = '', style = {} }: { 
    iconName: string; 
    className?: string; 
    style?: React.CSSProperties 
  }) => {
    return (
      <i 
        className={`bi bi-${iconName} ${className}`} 
        style={style}
        title={iconName}
      />
    );
  };

  return (
    <div className={sidePanelStyles.iconPicker}>
      <div className={sidePanelStyles.selectedIconDisplay}>
        <IconComponent 
          iconName={selectedIcon} 
          style={{ fontSize: '1.5rem', color: 'var(--primary)' }} 
        />
        <span className={sidePanelStyles.iconName}>
          {getSelectedIconInfo().label}
        </span>
      </div>
      
      <div className={sidePanelStyles.iconSearchContainer}>
        <input
          type="text"
          className={sidePanelStyles.formControl}
          placeholder="Search icons..."
          value={iconSearchTerm}
          onChange={(e) => setIconSearchTerm(e.target.value)}
        />
      </div>
      
      <div className={sidePanelStyles.categoryFilters}>
        {getIconCategories().map(category => (
          <button
            key={category.value}
            type="button"
            className={`${sidePanelStyles.categoryFilter} ${selectedIconCategory === category.value ? sidePanelStyles.active : ''}`}
            onClick={() => setSelectedIconCategory(category.value)}
          >
            {category.label}
          </button>
        ))}
      </div>
      
      <div className={sidePanelStyles.iconSuggestions}>
        {filteredIcons.map(icon => (
          <div
            key={icon.name}
            className={`${sidePanelStyles.iconOption} ${selectedIcon === icon.name ? sidePanelStyles.selected : ''}`}
            onClick={() => onIconSelect(icon.name)}
            title={icon.label}
          >
            <IconComponent iconName={icon.name} />
            <div className={sidePanelStyles.iconLabel}>{icon.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IconPicker;

